import { useEffect, useState } from 'react';
import { observer } from 'mobx-react-lite';
import {
    Alert,
    Box,
    Button,
    Chip,
    CircularProgress,
    Dialog,
    DialogActions,
    DialogContent,
    DialogTitle,
    List,
    Typography,
} from '@mui/material';

import AdminNodeStore from '../../../stores/admin-node-store';
import { DeviceService } from '../../../http/device-service';
import { NodeService } from '../../../http/node-service';
import { DbNode } from '../../../types/node';
import { DeviceInfo, DeviceType } from '../../../types/device';
import NodePickerItem from '../NodePickerItem';
import DeviceName from '../../dashboard/device/DeviceName';

//=================================================================================================
interface EditDeviceDialogProps {
    device: DeviceInfo;
    open: boolean;
    onClose: () => void;
    onSaved: () => void;
    rootNodeId: number;
}

const EditDeviceDialog = observer(({ device, open, onClose, onSaved, rootNodeId }: EditDeviceDialogProps) => {
    const [rootStore] = useState(() => new AdminNodeStore(rootNodeId));
    const [selectedNode, setSelectedNode] = useState<DbNode | null>(null);
    const [nodeLoading, setNodeLoading] = useState(false);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState('');

    const info = device.info ?? {};
    const deviceType = (info.deviceType as DeviceType) ?? DeviceType.Device_Unknown;

    useEffect(() => {
        if (!open) return;
        setError('');
        setSelectedNode(null);
        setNodeLoading(true);
        rootStore.loadNode().catch(() => {});
        NodeService.getById(device.node_id)
            .then((res) => setSelectedNode(res.data))
            .catch(() => {})
            .finally(() => setNodeLoading(false));
    }, [open, device]); // eslint-disable-line react-hooks/exhaustive-deps

    const handleClose = () => {
        if (loading) return;
        setError('');
        onClose();
    };

    const handleSubmit = async () => {
        if (!selectedNode || selectedNode.node_id === device.node_id) {
            handleClose();
            return;
        }
        setLoading(true);
        setError('');
        try {
            await DeviceService.move({ device_id: device.device_id, node_id: selectedNode.node_id });
            onSaved();
            handleClose();
        } catch {
            setError('Не удалось сохранить');
        } finally {
            setLoading(false);
        }
    };

    return (
        <Dialog
            open={open}
            onClose={handleClose}
            fullWidth
            maxWidth="xs"
            onKeyDown={(e) => {
                if (e.key === 'Enter' && !e.shiftKey && !loading) handleSubmit();
            }}
        >
            <DialogTitle>
                <DeviceName deviceType={deviceType} id={device.device_id} />
            </DialogTitle>
            <DialogContent sx={{ display: 'flex', flexDirection: 'column', gap: 2, pt: '16px !important' }}>
                <Box>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 0.5 }}>
                        <Typography variant="caption" color="text.secondary">
                            Нода
                        </Typography>
                        {nodeLoading ? (
                            <CircularProgress size={12} />
                        ) : selectedNode ? (
                            <Chip label={selectedNode.name} size="small" onDelete={() => setSelectedNode(null)} />
                        ) : null}
                    </Box>
                    <Box
                        sx={{
                            border: '1px solid',
                            borderColor: 'divider',
                            borderRadius: 1,
                            maxHeight: 200,
                            overflowY: 'auto',
                        }}
                    >
                        {rootStore.loading && rootStore.items.length === 0 ? (
                            <Box sx={{ display: 'flex', justifyContent: 'center', py: 2 }}>
                                <CircularProgress size={20} />
                            </Box>
                        ) : (
                            <List dense disablePadding sx={{ p: 0.5 }}>
                                {rootStore.items.map((node) => (
                                    <NodePickerItem
                                        key={node.node_id}
                                        node={node}
                                        depth={0}
                                        selectedNodeId={selectedNode?.node_id ?? null}
                                        onSelect={setSelectedNode}
                                    />
                                ))}
                            </List>
                        )}
                    </Box>
                </Box>
                {error && <Alert severity="error">{error}</Alert>}
            </DialogContent>
            <DialogActions>
                <Button onClick={handleClose} disabled={loading}>
                    Отмена
                </Button>
                <Button onClick={handleSubmit} variant="contained" disabled={loading} disableElevation>
                    {loading ? <CircularProgress size={18} /> : 'Сохранить'}
                </Button>
            </DialogActions>
        </Dialog>
    );
});

export default EditDeviceDialog;
